import React, {useEffect, useState} from "react"
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Modal,
    ModalClose,
    ModalDialog,
    Typography
} from "@mui/joy"
import {Download, Share2} from "lucide-react"
import {useTranslation} from "react-i18next"
import {drawResultCard, ResultCardContent} from "../../utils/resultCard"

type ShareResultDialogProps = {
    open?: boolean
    onClose?: () => void
    content: ResultCardContent
    /** Used for the downloaded file's name. */
    fileName: string
}

/**
 * Preview of the shareable result card, with the device's share sheet where it can share
 * images (most phones) and a plain download everywhere.
 */
const ShareResultDialog = (props: ShareResultDialogProps) => {
    const {t} = useTranslation()
    const [image, setImage] = useState<{ blob: Blob, url: string }>()
    const [failed, setFailed] = useState(false)
    const file = image && new File([image.blob], `${props.fileName}.png`, {type: "image/png"})
    const canShare = !!file && typeof navigator.canShare === "function" && navigator.canShare({files: [file]})

    useEffect(() => {
        if (!props.open) return
        let url: string
        setFailed(false)
        drawResultCard(props.content)
            .then(blob => {
                url = URL.createObjectURL(blob)
                setImage({blob, url})
            })
            .catch(() => setFailed(true))
        return () => {
            if (url) URL.revokeObjectURL(url)
            setImage(undefined)
        }
    }, [props.open])

    const handleShare = async () => {
        try {
            await navigator.share({files: [file], title: props.content.testTitle, text: t("shareResultsText", {
                score: props.content.score, test: props.content.testTitle
            })})
        } catch {
            // Closing the share sheet rejects; nothing to report.
        }
    }

    const handleDownload = () => {
        const link = document.createElement("a")
        link.href = image.url
        link.download = `${props.fileName}.png`
        link.click()
    }

    return <Modal open={!!props.open} onClose={props.onClose}>
        <ModalDialog variant="outlined" sx={{width: {xs: "100%", sm: "30rem"}, maxWidth: "calc(100vw - 2rem)", boxSizing: "border-box"}}>
            <ModalClose/>
            <DialogTitle>{t("shareResultsDialogTitle")}</DialogTitle>
            <DialogContent sx={{alignItems: "center"}}>
                {image ?
                    <Box component="img" src={image.url} alt={t("shareResultsDialogTitle")}
                         sx={{width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 12, border: "1px solid", borderColor: "divider"}}/> :
                    failed ?
                        <Typography level="body-sm" color="danger">{t("shareResultsImageError")}</Typography> :
                        <CircularProgress sx={{my: 6}}/>}
            </DialogContent>
            <DialogActions>
                {canShare &&
                    <Button startDecorator={<Share2/>} onClick={handleShare}>
                        {t("shareResultsShareActionLabel")}
                    </Button>}
                <Button variant={canShare ? "outlined" : "solid"} startDecorator={<Download/>}
                        disabled={!image} onClick={handleDownload}>
                    {t("shareResultsDownloadActionLabel")}
                </Button>
            </DialogActions>
        </ModalDialog>
    </Modal>
}

export default ShareResultDialog
